import React, { useEffect, useRef } from 'react';
import { formatDateTime, formatDateTimeToHHmm } from '../../common/StaticExports';
import { useTranslation } from 'react-i18next';
import { Box, ClickAwayListener, Fade, Paper, Popper } from '@mui/material';
import './styles.css';

export const ActivityHistoryPopup = ({ 
  data, 
  isVisible, 
  onClose, 
  anchorElement
}:any) => {
  const [t] = useTranslation();

  // Render Lucide icon by name
  const renderIcon = (iconName: string) => {
    try {
      const LucideIcons = require('lucide-react');
      const IconComponent = LucideIcons[iconName];
      if (IconComponent) {
        return React.createElement(IconComponent, { size: 16 });
      }
    } catch (error) {
      console.warn('Lucide React not available, falling back to text');
    }
    
    // Fallback to text/unicode icons if Lucide React is not available
    const fallbackIcons: { [key: string]: string } = {
      'MessageCircle': '💬',
      'Edit3': '✏️',
      'Trash2': '🗑️',
      'RotateCcw': '↻'
    };
    
    return <span>{fallbackIcons[iconName] || '•'}</span>;
  };

  // Determine activity type and get appropriate icon names
  const getActivityDetails = (item:any, index:any) => {
    const isDeleted = (!item.updatedComment || item.updatedComment.trim() === '');
    const isFirstEntry = index === 0;
    const justCreated = (isFirstEntry && item.updatedComment && item.updatedComment.trim() !== '' && (item.previousComment === null || item.previousComment === undefined || item.previousComment.trim() === ''));
    const isRecreated = ((!item.previousComment || item.previousComment.trim() === '') && 
                        (item.updatedComment && item.updatedComment.trim() !== '') && 
                        !isFirstEntry) ||
                        (index > 0 && data[index - 1] && 
                        (!data[index - 1].updatedComment || data[index - 1].updatedComment.trim() === '') &&
                        (item.updatedComment && item.updatedComment.trim() !== ''));
    
    if (isFirstEntry && justCreated) {
      return {
        type: 'created',
        iconName: 'MessageCircle',
        colorClass: 'AHP-icon-created',
        label: 'Created'
      };
    } else if (isDeleted) {
      return {
        type: 'deleted',
        iconName: 'Trash2',
        colorClass: 'AHP-icon-deleted',
        label: 'Deleted'
      };
    } else if (isRecreated) {
      return {
        type: 'recreated',
        iconName: 'RotateCcw',
        colorClass: 'AHP-icon-recreated',
        label: 'Re-created'
      };
    } else {
      return {
        type: 'edited',
        iconName: 'Edit3',
        colorClass: 'AHP-icon-edited',
        label: 'Edited'
      };
    }
  };

  if (!isVisible || !data || data.length === 0) {
    return null;
  }

  const handleClickInsidePopper = (event: any) => {
    event.stopPropagation();
  }

  // Custom positioning logic to prevent overflow
  const getPopperModifiers = () => [
    {
      name: 'preventOverflow',
      enabled: true,
      options: {
        boundary: 'viewport',
        padding: 16, // 16px padding from viewport edges
      },
    },
    {
      name: 'flip',
      enabled: true,
      options: {
        fallbackPlacements: ['bottom-start', 'bottom-end', 'top-start', 'top-end', 'right-start', 'left-start'],
      },
    },
    {
      name: 'offset',
      enabled: true,
      options: {
        offset: [0, 8], // 8px offset from anchor element
      },
    },
  ];

  return (
    <Popper
      open={isVisible} 
      anchorEl={anchorElement}
      placement="bottom-start"
      transition 
      modifiers={getPopperModifiers()}
      style={{ zIndex: 1300 }} // High z-index to appear above other elements
    >
      {({ TransitionProps }) => (
        <ClickAwayListener onClickAway={onClose}>
          <Fade {...TransitionProps} timeout={350}>
            <Paper 
              elevation={8}
              onClick={handleClickInsidePopper}
              className="AHP-popup-paper"
            >
              <Box className="AHP-popup-container">
                {/* Header */}
                <div className="AHP-header">
                  <div className="AHP-header-content">
                    <h3 className="AHP-header-title">{t('COMMENT_HISTORY')}</h3>
                    <button
                      onClick={onClose}
                      className="AHP-close-button"
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Activity List */}
                <div className="AHP-activity-list">
                  <div className="AHP-activity-content">
                    {data.map((item:any, index:any) => {
                      const activityDetails = getActivityDetails(item, index);
                      const userName = `${item.firstName} ${item.lastName}`;
                      
                      return (
                        <div key={index} className="AHP-activity-item">
                          {/* Activity Icon */}
                          <div className={`AHP-activity-icon ${activityDetails.colorClass}`}>
                            {renderIcon(activityDetails.iconName)}
                          </div>
                          
                          {/* Activity Details */}
                          <div className="AHP-activity-details">
                            <div className="AHP-activity-header">
                              <span className="AHP-activity-label">
                                {activityDetails.label}
                              </span>
                              <span className="AHP-activity-author">
                                by {userName} {formatDateTime(item.updatedDate)}
                              </span>
                            </div>
                            
                            {/* Comment Content - only show if not deleted */}
                            {item.updatedComment && item.updatedComment.trim() !== '' && (
                              <div className="AHP-comment-content">
                                {item.updatedComment}
                              </div>
                            )}
                            
                            {/* Deleted indicator */}
                            {(!item.updatedComment || item.updatedComment.trim() === '') && (
                              <div className="AHP-deleted-indicator">
                                Comment deleted
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Box>
            </Paper>
          </Fade>
        </ClickAwayListener>
      )}
    </Popper>
  );
};